import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { BenefitReviewStatus, type ProjectGovernanceDto } from '@paladin-monorepo/common';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { monitoringQueryKey, projectMonitoringApi } from '@/lib/api/project-monitoring';
import { getAppFormatLocale } from '@/utils/locale';

export function ProjectMonitoringCard({
  projectId,
  governance,
}: {
  projectId: string;
  governance: ProjectGovernanceDto;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showHighlight, setShowHighlight] = useState(false);
  const { data } = useQuery({
    queryKey: monitoringQueryKey(projectId),
    queryFn: () => projectMonitoringApi.get(projectId),
  });
  const ack = useMutation({
    mutationFn: (alertId: string) => projectMonitoringApi.acknowledge(projectId, alertId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: monitoringQueryKey(projectId) }),
  });

  if (!data) return null;
  const lastCheck = data.lastCheckAt
    ? new Intl.DateTimeFormat(getAppFormatLocale(), { dateStyle: 'short', timeStyle: 'short' }).format(
        new Date(data.lastCheckAt),
      )
    : t('workspace.governance.monitoring.never');

  return (
    <section className="space-y-3 lg:col-span-2">
      <h4 className="text-sm font-medium">{t('workspace.governance.monitoring.title')}</h4>
      <p className="text-sm text-muted-foreground">
        {t('workspace.governance.monitoring.lastCheck', { date: lastCheck })}
      </p>
      {data.failedDeliveries.length > 0 && (
        <p className="text-sm text-destructive">{t('workspace.governance.monitoring.failedDelivery')}</p>
      )}
      {data.alerts.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('workspace.governance.monitoring.noAlerts')}</p>
      ) : (
        <ul className="space-y-2">
          {data.alerts.map((alert) => (
            <li key={alert.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <span>
                {alert.displayTitle ?? t(`dashboard.portfolio.alerts.kind.${alert.kind}`)}
                {alert.displayBody && (
                  <span className="block text-xs text-muted-foreground">{alert.displayBody}</span>
                )}
                {alert.payload.autoAction === false && (
                  <span className="ml-1 text-muted-foreground">
                    ({t('workspace.governance.monitoring.noAutoAction')})
                  </span>
                )}
              </span>
              <span className="flex items-center gap-2">
                <Badge variant={alert.status === 'OPEN' ? 'destructive' : 'secondary'}>
                  {t(`dashboard.portfolio.alerts.status.${alert.status}`)}
                </Badge>
                {alert.status === 'OPEN' &&
                  (governance.permissions.isProjectManager || governance.permissions.isExecutive) && (
                    <Button size="sm" variant="outline" onClick={() => ack.mutate(alert.id)} disabled={ack.isPending}>
                      {t('workspace.governance.monitoring.acknowledge')}
                    </Button>
                  )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {data.highlight && (
        <div>
          <Button size="sm" variant="outline" onClick={() => setShowHighlight((v) => !v)}>
            {t('workspace.governance.monitoring.highlight')}
          </Button>
          {showHighlight && (
            <div className="mt-2 space-y-2 rounded-md bg-muted p-3 text-sm">
              {data.highlight.narrativeMarkdown ? (
                <div className="whitespace-pre-wrap">{data.highlight.narrativeMarkdown}</div>
              ) : (
                <p className="text-xs text-muted-foreground">{t('workspace.governance.monitoring.narrativeUnavailable')}</p>
              )}
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">
                  {t('workspace.governance.monitoring.highlightFacts')}
                </summary>
                <pre className="mt-2 max-h-48 overflow-auto">{JSON.stringify(data.highlight, null, 2)}</pre>
              </details>
            </div>
          )}
        </div>
      )}
      {data.benefitReviews.length > 0 && (
        <div className="space-y-2">
          <h5 className="text-sm font-medium">{t('workspace.governance.monitoring.benefitReviews')}</h5>
          {data.benefitReviews.map((review) => (
            <BenefitReviewRow key={review.id} projectId={projectId} review={review} canRecord={governance.permissions.isProjectManager || governance.permissions.isBusinessLayer} />
          ))}
        </div>
      )}
    </section>
  );
}

function BenefitReviewRow({
  projectId,
  review,
  canRecord,
}: {
  projectId: string;
  review: { id: string; dueDate: string; status: string; outcome: string | null };
  canRecord: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const schema = yup.object({ outcome: yup.string().trim().required() }).required();
  const { register, handleSubmit } = useForm({ resolver: yupResolver(schema) });
  const mutation = useMutation({
    mutationFn: (outcome: string) => projectMonitoringApi.recordBenefitReview(projectId, review.id, { outcome }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: monitoringQueryKey(projectId) }),
  });
  return (
    <div className="rounded-md border p-3 text-sm">
      <p>
        {review.dueDate} · {t(`workspace.governance.monitoring.reviewStatus.${review.status}`)}
      </p>
      {review.outcome && <p className="whitespace-pre-wrap">{review.outcome}</p>}
      {canRecord && review.status === BenefitReviewStatus.Pending && (
        <form className="mt-2 space-y-2" onSubmit={handleSubmit((v) => mutation.mutate(v.outcome))}>
          <Textarea {...register('outcome')} />
          <Button type="submit" size="sm" disabled={mutation.isPending}>
            {t('workspace.governance.monitoring.record')}
          </Button>
        </form>
      )}
    </div>
  );
}
